import { FC, useCallback, useState } from 'react';
import { InfoDrawer } from '~/shared/InfoDrawer';
import { Text } from '~/shared/components';
import { useForm } from 'react-hook-form';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useRouter } from 'next/router';
import { useTranslation } from '~/shared/utils/translation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidationSchema } from './lib/useValidationSchema';
import { LoginInputs } from './models/fields';
import { notifyInfo } from '~/shared/utils/errorBoundary/toast';

import {
    StyledLoginSignupWrapper,
    StyledImercoPlusWrapper,
    StyledLogo,
    StyledLoginButton,
    StyledSignupButton,
} from './styled';
import { LoginDrawerMode } from './hooks/useLoginDrawerState';
import { useFrame } from '~/shared/utils';
import { usePage } from '~/templates/pages';
import {
    LoginView,
    CreateLoginView,
    ForgotPasswordView,
    ForgotPasswordSubmittedView,
    ForgotPasswordSetNewView,
    ChangePasswordView,
    MessageView,
} from './components';
import { theme } from '~/theme';

type LoginDrawerProps = {
    open: boolean;
    close: () => void;
    loginDrawerMode: LoginDrawerMode;
    setLoginDrawerMode: (mode: LoginDrawerMode) => void;
};

const {
    LOGIN,
    CREATE_LOGIN,
    FORGOT_PASSWORD,
    FORGOT_PASSWORD_ERROR,
    FORGOT_PASSWORD_SUBMITTED,
    FORGOT_PASSWORD_SET_NEW,
    CHANGE_PASSWORD,
    CHANGE_PASSWORD_DONE,
    CHANGE_PASSWORD_FAIL,
} = LoginDrawerMode;

export const LoginDrawer: FC<LoginDrawerProps> = ({
    open,
    close,
    loginDrawerMode,
    setLoginDrawerMode,
}) => {
    const { login, updatePassword, requestResetPassword, setPasswordFromToken, logout } =
        useCustomer();
    const { translate } = useTranslation();
    const { query, replace } = useRouter();
    const { data: frame } = useFrame();
    const page = usePage();

    const [authenticationError, setAuthenticationError] = useState<string>('');
    const [isValidatingLogin, setIsValidatingLogin] = useState(false);
    const validationSchema = useValidationSchema();
    const useResetPasswordValidationSchema = useValidationSchema();
    const methods = useForm<LoginInputs>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: yupResolver(validationSchema),
    });
    const resetPasswordMethods = useForm<LoginInputs>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: yupResolver(useResetPasswordValidationSchema),
    });

    const loginViewHandler = () => setLoginDrawerMode(LOGIN);
    const signupViewHandler = () => setLoginDrawerMode(CREATE_LOGIN);

    const handleLoginSubmit = async (payload: LoginInputs) => {
        const request = {
            email: payload.email,
            password: payload.password,
        };
        try {
            setIsValidatingLogin(true);
            await login(request);
            setIsValidatingLogin(false);
            setAuthenticationError('');
            closeAndReset();
            if (query.basketid) {
                window.location.href = frame.settings?.pageReferences.basketPage?.url ?? '/';
            } else if (page.type === 'receiptPage') {
                window.location.href = '/';
            } else {
                window.location.reload();
            }
        } catch (e) {
            setIsValidatingLogin(false);
            setAuthenticationError(translate('login.loginRejected'));
        }
    };

    const handleResetSendEmail = async (payload: LoginInputs) => {
        try {
            await requestResetPassword(payload.email);
            setLoginDrawerMode(FORGOT_PASSWORD_SUBMITTED);
        } catch (e) {
            setLoginDrawerMode(FORGOT_PASSWORD_ERROR);
        } finally {
            reset();
        }
    };

    const handleResetPasswordFromToken = async (payload: LoginInputs) => {
        if (!query.resettoken) {
            closeAndReset();
            return;
        }
        try {
            await setPasswordFromToken(
                (Array.isArray(query.email) ? query.email[0] : query.email)?.replace('TA_', '') ??
                    '',
                query.resettoken as string,
                payload.newpassword,
            );
            setLoginDrawerMode(CHANGE_PASSWORD_DONE);
        } catch (e) {
            setLoginDrawerMode(CHANGE_PASSWORD_FAIL);
        } finally {
            reset();
            replace('/', undefined, { shallow: true });
        }
    };

    const setNewPassword = async (payload: LoginInputs) => {
        const request = {
            currentPassword: payload.password,
            password: payload.newpassword,
        };
        await updatePassword(request);
        await logout();
        notifyInfo(translate('login.changePasswordDone'));
        closeAndReset();
    };

    const shouldShowLoginSignup =
        loginDrawerMode !== FORGOT_PASSWORD_ERROR &&
        loginDrawerMode !== FORGOT_PASSWORD_SUBMITTED &&
        loginDrawerMode !== CHANGE_PASSWORD;

    const reset = () => {
        methods.reset();
        resetPasswordMethods.reset();
    };

    const closeAndReset = useCallback(() => {
        reset();
        close();
    }, []);

    const renderAuthenticationError = (errorMessage: string): JSX.Element => (
        <Text
            data-testid="auth-error"
            css={{ color: 'red', margin: `${theme.space[3]} 0` }}
            variant={'caption'}
        >
            {errorMessage}
        </Text>
    );

    return (
        <InfoDrawer open={open} close={closeAndReset}>
            {shouldShowLoginSignup && (
                <>
                    <StyledImercoPlusWrapper>
                        <StyledLogo />
                        <span>{translate('login.slogan')}</span>
                    </StyledImercoPlusWrapper>
                    <StyledLoginSignupWrapper loginDrawerMode={loginDrawerMode}>
                        <StyledLoginButton
                            loginDrawerMode={loginDrawerMode}
                            role="button"
                            tabIndex={0}
                            onClick={loginViewHandler}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    loginViewHandler();
                                }
                            }}
                        >
                            {translate('login.login')}
                        </StyledLoginButton>
                        <StyledSignupButton
                            loginDrawerMode={loginDrawerMode}
                            role="button"
                            tabIndex={0}
                            onClick={signupViewHandler}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    signupViewHandler();
                                }
                            }}
                        >
                            {translate('login.becomeMember')}
                        </StyledSignupButton>
                    </StyledLoginSignupWrapper>
                </>
            )}

            {loginDrawerMode === LOGIN && (
                <LoginView
                    methods={methods}
                    handleLoginSubmit={handleLoginSubmit}
                    authenticationError={authenticationError}
                    renderAuthenticationError={renderAuthenticationError}
                    setLoginDrawerMode={setLoginDrawerMode}
                    translate={translate}
                    isValidatingLogin={isValidatingLogin}
                />
            )}

            {loginDrawerMode === CREATE_LOGIN && (
                <CreateLoginView
                    setLoginDrawerMode={setLoginDrawerMode}
                    closeAndReset={closeAndReset}
                    translate={translate}
                />
            )}

            {loginDrawerMode === FORGOT_PASSWORD && (
                <ForgotPasswordView
                    methods={methods}
                    handleResetSendEmail={handleResetSendEmail}
                    translate={translate}
                />
            )}
            {loginDrawerMode === FORGOT_PASSWORD_SUBMITTED && (
                <ForgotPasswordSubmittedView translate={translate} />
            )}
            {loginDrawerMode === FORGOT_PASSWORD_SET_NEW && (
                <ForgotPasswordSetNewView
                    handleResetPasswordFromToken={handleResetPasswordFromToken}
                    authenticationError={authenticationError}
                    renderAuthenticationError={renderAuthenticationError}
                    translate={translate}
                />
            )}
            {loginDrawerMode === FORGOT_PASSWORD_ERROR && (
                <MessageView
                    header={translate('createAccount.changePassword')}
                    body={translate('login.errorCreatingPassword')}
                />
            )}
            {loginDrawerMode === CHANGE_PASSWORD && (
                <ChangePasswordView
                    methods={methods}
                    setNewPassword={setNewPassword}
                    authenticationError={authenticationError}
                    renderAuthenticationError={renderAuthenticationError}
                    translate={translate}
                />
            )}
            {loginDrawerMode === CHANGE_PASSWORD_FAIL && (
                <MessageView body={translate('login.changePasswordFail')} />
            )}
            {loginDrawerMode === CHANGE_PASSWORD_DONE && (
                <MessageView body={translate('login.changePasswordDone')} />
            )}
        </InfoDrawer>
    );
};
